import React, { useState, useMemo } from 'react';
import cn from 'classnames';

import {
  PageTitle,
  Table,
  Button,
  Shimmer,
  Checkbox,
  Loader,
  PageIntro,
  EntitlementMeter,
} from 'src/components';
import { useAssetSchemasListQuery, useSchemaPresetsQuery } from 'src/api/queries/schemesQueries';
import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';
import { useQueryClient } from 'react-query';
import { queryNames } from 'src/api/utils/queryNames';
import { SchemaStatusFilter, setSchemaStatus } from 'src/api/functions/schemaApi';
import { useCreateSchemaFromPresetsMutation, useDeleteSchemaMutation } from 'src/api/mutations/schemaMutations';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { AssetSchema, SchemaPresetSummary, SchemaPresetCategory } from 'src/types/TSchemes';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { Pencil, Trash, Plus, Stack, Eye, Pause, Play } from 'src/common/icons';
import SchemaPresetsModal from 'src/components/SchemaPresetsModal';
import SchemaEditorModal from 'src/components/SchemaEditorModal';
import { Link } from 'react-router-dom';

import styles from './styles.module.scss';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';

// The backend's schema-cap message says "Delete one to swap presets", but a
// schema can't be deleted until it's deactivated — so point at the button that
// actually starts that, rather than at a step the customer can't find.
const CAP_HINT =
  ' Use the pause button on a schema you no longer need to deactivate it — ' +
  'that frees a slot immediately, and you can delete it afterwards.';

function withCapHint(message: string): string {
  return message.includes('schema_limit_reached') ? message + CAP_HINT : message;
}

// Plan-gate 403s and structured 400s (e.g. the per-plan schema cap) carry an
// actionable message — surface it instead of the generic "Something went
// wrong". Returns null when the error carries nothing structured (call sites
// fall back to getErrorMessage).
function schemaErrorMessage(error: any): string | null {
  const gate = planUpgradeRequired(error);
  if (gate?.message) return gate.message;
  const body = error?.error;
  const errors = body && typeof body === 'object' && (body as any).data?.errors;
  if (Array.isArray(errors) && errors.length) return withCapHint(errors.join(', '));
  // Plain-string 400 bodies (e.g. the status endpoint's cap refusal).
  if (typeof body === 'string' && body.includes('schema_limit_reached')) {
    return withCapHint(body);
  }
  return null;
}


// Category labels for display
const CATEGORY_INFO: Record<SchemaPresetCategory, { label: string; description: string }> = {
  core: {
    label: 'Core Templates',
    description: 'Essential schemas for common asset management workflows',
  },
  industry: {
    label: 'Industry Templates',
    description: 'Pre-built schemas for common industry use cases',
  },
  extended: {
    label: 'Extended Templates',
    description: 'Additional schemas for advanced workflows',
  },
};

// Feature labels for display
const FEATURE_LABELS: Record<string, string> = {
  'approval-workflow': 'Approval Workflow',
  'rights-tracking': 'Rights Tracking',
  'expiry-management': 'Expiry Management',
  'venue-management': 'Venue Management',
  'capture-point-tracking': 'Capture Points',
  'guest-photo-lifecycle': 'Guest Photos',
  'partner-branding': 'Partner Branding',
  'multi-venue-operations': 'Multi-Venue',
};

// Format feature slug to human-readable label as fallback
const formatFeatureLabel = (feature: string): string =>
  feature
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const SchemaSettings: React.FC = () => {
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { showModal } = useModal();

  const accessToken = useAccessToken();

  // State for status filter tab
  const [statusFilter, setStatusFilter] = useState<SchemaStatusFilter>('ACTIVE');
  // State for inline preset selection (empty state)
  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(new Set());
  // State for status toggle loading
  const [togglingSchemaId, setTogglingSchemaId] = useState<number | null>(null);
  // State for expanded schema detail
  const [expandedSchemaId, setExpandedSchemaId] = useState<number | null>(null);

  // Main query for the currently selected status tab
  const {
    data: schemasResult,
    isLoading,
    refetch,
  } = useAssetSchemasListQuery(statusFilter);

  // Background query for the other tab's count
  const {
    data: activeCountResult,
    refetch: refetchActive,
  } = useAssetSchemasListQuery('ACTIVE');
  const {
    data: inactiveCountResult,
    refetch: refetchInactive,
  } = useAssetSchemasListQuery('INACTIVE');

  const activeCount = (activeCountResult?.data ?? []).length;
  const inactiveCount = (inactiveCountResult?.data ?? []).length;

  // Fetch presets for empty state
  const { data: presetsResult, isLoading: isLoadingPresets } = useSchemaPresetsQuery();
  const presets = presetsResult?.data?.presets ?? [];

  // Group presets by category
  const groupedPresets = useMemo(() => {
    const grouped: Record<SchemaPresetCategory, SchemaPresetSummary[]> = {
      core: [],
      industry: [],
      extended: [],
    };

    presets.forEach((preset) => {
      if (grouped[preset.category]) {
        grouped[preset.category].push(preset);
      }
    });

    return grouped;
  }, [presets]);

  // Live-count cap (plan_quotas `asset_schemas`, includes purchased extras).
  // quota 0 == unlimited. Drives the proactive counter + disabled create
  // buttons; the backend gate stays the enforcement (this is UX, not
  // security).
  const { data: limits } = usePlanLimitsQuery();
  const queryClient = useQueryClient();
  const schemaLimit = limits?.asset_schemas;
  const atSchemaCap = Boolean(
    schemaLimit && schemaLimit.quota > 0 && schemaLimit.used >= schemaLimit.quota,
  );
  // Tooltip for the one control that IS still disabled at the cap — authoring
  // a schema from scratch, which has nothing to show a customer who cannot
  // save it. (Browsing presets stays open precisely because it does.) The
  // counter's own wording lives in `EntitlementMeter`; the meter carries the
  // "Add schemas / Compare plans" link, so this only has to explain the
  // disabled button.
  const capTitle = atSchemaCap
    ? `You're using all ${schemaLimit?.quota} schema`
      + `${schemaLimit?.quota === 1 ? '' : 's'} your plan includes. `
      + 'Pause or delete one to free capacity'
      + ((schemaLimit?.max_extra ?? 0) > 0
        ? ', or add more from the Subscription page.'
        : ', or upgrade your plan.')
    : undefined;

  // Delayed refetch to handle eventual consistency across backend servers
  // Refetches both active and inactive queries so tab counts stay in sync
  const delayedRefetch = (delay = 1000) => {
    setTimeout(() => {
      refetch();
      refetchActive();
      refetchInactive();
      // Counted things changed — refresh the cap counter too.
      queryClient.invalidateQueries([queryNames.planLimits]);
    }, delay);
  };

  const deleteSchemaMutation = useDeleteSchemaMutation({
    onSuccess: () => {
      showSuccess('Schema deleted successfully');
      setExpandedSchemaId(null);
      delayedRefetch();
    },
    onError: (error: any) => {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    },
  });

  const addPresetsMutation = useCreateSchemaFromPresetsMutation({
    onSuccess: (response) => {
      const { created, errors } = response.data;

      if (errors && errors.length > 0) {
        showAlert(`Added ${created} schema(s). Some failed: ${errors.join(', ')}`);
      } else {
        showSuccess(`Successfully added ${created} schema${created !== 1 ? 's' : ''}`);
      }

      setSelectedPresets(new Set());
      delayedRefetch(1500);
    },
    onError: (error: any) => {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    },
  });

  const handleAddPresets = () => {
    showModal(
      <SchemaPresetsModal
        onSuccess={() => {
          delayedRefetch(1500);
        }}
      />,
    );
  };

  const handleCreateSchema = () => {
    showModal(
      <SchemaEditorModal
        onSuccess={() => {
          delayedRefetch(1500);
        }}
      />,
    );
  };

  const handleEditSchema = (schema: AssetSchema) => {
    showModal(
      <SchemaEditorModal
        schema={schema}
        onSuccess={() => {
          delayedRefetch();
        }}
      />,
    );
  };

  const handleDeleteSchema = (schemaId: number) => {
    if (window.confirm('Are you sure you want to delete this schema?')) {
      deleteSchemaMutation.mutate(schemaId);
    }
  };

  const handleToggleStatus = async (schema: AssetSchema) => {
    const newStatus = schema.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const action = newStatus === 'INACTIVE' ? 'deactivate' : 'reactivate';

    const confirmMessage =
      newStatus === 'INACTIVE'
        ? `Deactivate "${schema.name?.en || `Schema #${schema.id}`}"?\n\n` +
          `Assets already using it keep their metadata. It stops being offered ` +
          `for new uploads, frees a schema slot on your plan, and can be ` +
          `reactivated or deleted afterwards.`
        : `Reactivate "${schema.name?.en || `Schema #${schema.id}`}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTogglingSchemaId(schema.id);
    try {
      // Status-only endpoint. This used to call `updateSchema`, which posts the
      // whole schema definition to `/api/v1/schema` — a route behind the
      // CUSTOM_SCHEMAS (Enterprise) gate. So on every non-Enterprise plan this
      // button 403'd, which was the trap: `delete` refuses an ACTIVE schema, so
      // a customer at their schema cap could not deactivate, could not delete,
      // and could not act on the cap error's own advice.
      const result = await setSchemaStatus(accessToken, schema.id, newStatus);
      if ('error' in result) {
        throw result;
      }
      showSuccess(
        newStatus === 'INACTIVE'
          ? 'Schema deactivated — a schema slot is now free.'
          : 'Schema activated successfully',
      );
      delayedRefetch();
    } catch (error: any) {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    } finally {
      setTogglingSchemaId(null);
    }
  };

  const handleToggleExpand = (schemaId: number) => {
    setExpandedSchemaId((prev) => (prev === schemaId ? null : schemaId));
  };

  // Toggle preset selection (for empty state inline selection)
  const togglePreset = (presetId: string) => {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  };

  const selectAllPresets = () => {
    setSelectedPresets(new Set(presets.map((p) => p.preset_id)));
  };

  const handleAddSelectedPresets = () => {
    if (selectedPresets.size === 0) return;
    addPresetsMutation.mutate(Array.from(selectedPresets));
  };

  const schemas: AssetSchema[] = schemasResult?.data ?? [];
  const hasSchemas = schemas.length > 0;

  // Get field count for a schema
  const getFieldCount = (schema: AssetSchema) => {
    return Object.keys(schema.schema?.properties ?? {}).length;
  };

  // Render the expanded detail for a schema
  const renderSchemaDetail = (schema: AssetSchema) => {
    const properties = schema.schema?.properties ?? {};
    const propertyEntries = Object.entries(properties);

    return (
      <tr key={`detail-${schema.id}`}>
        <td colSpan={5}>
          <div className={styles.detailPanel}>
            <div className={styles.detailContent}>
              <div className={styles.fieldsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Field Key</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Searchable</th>
                      <th>Keyword Searchable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertyEntries
                      .sort(([, a]: [string, any], [, b]: [string, any]) =>
                        (a?.filespin_properties?.ui?.order ?? 999) -
                        (b?.filespin_properties?.ui?.order ?? 999)
                      )
                      .map(([key, prop]: [string, any]) => (
                        <tr key={key}>
                          <td><code>{key}</code></td>
                          <td>{prop?.filespin_properties?.title?.en || prop?.title || '-'}</td>
                          <td>
                            <span
                              className={styles.fieldDescription}
                              title={prop?.description || ''}
                            >
                              {prop?.description || '-'}
                            </span>
                          </td>
                          <td>
                            <span className={styles.typeBadge}>{prop?.type || '-'}</span>
                          </td>
                          <td>{prop?.filespin_properties?.searchable ? 'Yes' : 'No'}</td>
                          <td>{prop?.filespin_properties?.keyword_searchable ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {propertyEntries.length === 0 && (
                  <p className={styles.emptyFields}>No fields defined in this schema.</p>
                )}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Asset Schemas"
        className={styles.pageTitle}
        counter={schemas.length}
        rightContainer={
          <div className={styles.headerActions}>
            {hasSchemas && (
              // NEVER disabled, even at the cap. Seeing which schemas exist —
              // and what fields each one brings — is exactly what a customer
              // needs in order to judge whether more capacity is worth paying
              // for, and a greyed-out button tells them nothing. The catalog
              // itself goes read-only at the cap and carries the price.
              <Button
                size="small"
                mode="secondary"
                icon={<Stack />}
                onClick={handleAddPresets}
              >
                Browse Presets
              </Button>
            )}
            <Button
              size="small"
              icon={<Plus />}
              onClick={handleCreateSchema}
              disabled={atSchemaCap}
              tooltip={capTitle}
            >
              Create Schema
            </Button>
          </div>
        }
      />

      <div className={styles.schemaPageContainer}>
        <PageIntro
          overview={(
            <>
              Asset schemas are the fields your team searches on — SKU, campaign,
              season, usage rights, expiry. Anything you put in a schema becomes
              a search facet, a bulk-edit column, and a field the API and your
              integrations can read. Assets without one are findable by filename
              only, which is how the same photo ends up re-shot or re-licensed
              because nobody could find the original.
            </>
          )}
          points={[
            {
              label: 'Findable',
              text: 'every field becomes a filter and a facet in search.',
            },
            {
              label: 'Consistent',
              text: 'applied at upload, so assets arrive tagged instead of being tidied up later.',
            },
            {
              label: 'Governed',
              text: "rights holder, licence expiry and approval state live on the asset, so you know what's still safe to publish.",
            },
            {
              label: 'Portable',
              text: 'the same fields flow through the API, the Adobe panel, and your Make / Zapier / n8n workflows.',
            },
          ]}
          footnote={(
            <>
              Every asset falls back to the built-in Default schema, which is free
              and never counts against your plan. The default schema for new
              uploads is set in{' '}
              <Link to="/admin/settings/metadata">Metadata Settings</Link>.
            </>
          )}
        />

        <section className={styles.pageContent}>
          <div className={styles.listToolbar}>
            <div className={styles.statusFilterTabs}>
              <button
                className={cn(styles.statusFilterTab, { [styles.active]: statusFilter === 'ACTIVE' })}
                onClick={() => { setStatusFilter('ACTIVE'); setExpandedSchemaId(null); }}
              >
                Active{activeCount > 0 ? ` (${activeCount})` : ''}
              </button>
              <button
                className={cn(styles.statusFilterTab, { [styles.active]: statusFilter === 'INACTIVE' })}
                onClick={() => { setStatusFilter('INACTIVE'); setExpandedSchemaId(null); }}
              >
                Inactive{inactiveCount > 0 ? ` (${inactiveCount})` : ''}
              </button>
            </div>
            <EntitlementMeter
              dimension="asset_schemas"
              noun="schema"
              nounPlural="schemas"
            />
          </div>

          {isLoading ? (
            <div className={styles.loadingWrapper}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Shimmer key={i} w="100%" h="4rem" radius="0.25rem" />
              ))}
            </div>
          ) : !hasSchemas && statusFilter === 'INACTIVE' ? (
            <div className={styles.emptyInactiveState}>
              <p>No inactive schemas.</p>
            </div>
          ) : !hasSchemas && statusFilter === 'ACTIVE' ? (
            <div className={styles.emptyStateSetup}>
              <div className={styles.setupHeader}>
                <h3>Get Started with Asset Schemas</h3>
                <p>
                  Asset schemas define the metadata fields for your assets.
                  Select from pre-built industry presets or create a custom schema from scratch.
                </p>
              </div>

              {isLoadingPresets ? (
                <div className={styles.presetsLoading}>
                  <Loader />
                </div>
              ) : presets.length > 0 ? (
                <>
                  <div className={styles.selectAllRow}>
                    <button
                      type="button"
                      className={styles.selectAllButton}
                      onClick={selectAllPresets}
                    >
                      Select All ({presets.length})
                    </button>
                    {selectedPresets.size > 0 && (
                      <span className={styles.selectedCount}>
                        {selectedPresets.size} selected
                      </span>
                    )}
                  </div>

                  <div className={styles.presetsContainer}>
                    {(['core', 'industry', 'extended'] as SchemaPresetCategory[]).map((category) => {
                      const categoryPresets = groupedPresets[category];
                      if (!categoryPresets || categoryPresets.length === 0) return null;

                      return (
                        <div key={category} className={styles.categorySection}>
                          {category !== 'core' && (
                            <div className={styles.categoryHeader}>
                              <h4 className={styles.categoryTitle}>
                                {CATEGORY_INFO[category].label}
                              </h4>
                              <p className={styles.categoryDescription}>
                                {CATEGORY_INFO[category].description}
                              </p>
                            </div>
                          )}

                          <div className={styles.presetsGrid}>
                            {categoryPresets.map((preset) => (
                              <div
                                key={preset.preset_id}
                                className={cn(styles.presetCard, {
                                  [styles.selected]: selectedPresets.has(preset.preset_id),
                                })}
                                onClick={() => togglePreset(preset.preset_id)}
                              >
                                <div className={styles.presetHeader}>
                                  <Checkbox
                                    checked={selectedPresets.has(preset.preset_id)}
                                    onChange={() => togglePreset(preset.preset_id)}
                                  />
                                  <span className={styles.presetName}>
                                    {preset.name?.en || preset.schema_title}
                                  </span>
                                </div>
                                <p className={styles.presetDescription}>{preset.description}</p>
                                {preset.features && preset.features.length > 0 && (
                                  <div className={styles.presetMeta}>
                                    <div className={styles.featureBadges}>
                                      {preset.features.map((feature) => (
                                        <span key={feature} className={styles.featureBadge}>
                                          {FEATURE_LABELS[feature] || formatFeatureLabel(feature)}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles.setupActions}>
                    <Button
                      mode="primary"
                      icon={<Stack />}
                      onClick={handleAddSelectedPresets}
                      loading={addPresetsMutation.isLoading}
                      disabled={selectedPresets.size === 0 || addPresetsMutation.isLoading}
                    >
                      Add {selectedPresets.size > 0 ? `${selectedPresets.size} ` : ''}Selected Schema
                      {selectedPresets.size !== 1 ? 's' : ''}
                    </Button>
                    <Button mode="tertiary" icon={<Plus />} onClick={handleCreateSchema}>
                      Create from Scratch
                    </Button>
                  </div>
                </>
              ) : (
                <div className={styles.noPresetsState}>
                  <p>No schema presets available. Create one from scratch instead.</p>
                  <Button icon={<Plus />} onClick={handleCreateSchema}>
                    Create Schema
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table
              headers={['Name', 'Status', 'Fields', 'Schema ID', 'Actions']}
              className={styles.schemasTable}
            >
              {schemas.map((schema: AssetSchema) => (
                <React.Fragment key={schema.id}>
                  <tr
                    className={cn(styles.schemaRow, {
                      [styles.expanded]: expandedSchemaId === schema.id,
                    })}
                  >
                    <td>
                      <span className={styles.schemaName}>
                        {schema.name?.en || schema.schema?.title || `Schema #${schema.id}`}
                        {schema.id === 0 && (
                          <span className={styles.builtInSchemaNote}>
                            Built in and free — always available as the fallback for
                            assets with no schema of their own. Does not use a schema slot.
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span
                        className={cn(styles.statusBadge, {
                          [styles.active]: schema.status === 'ACTIVE',
                          [styles.inactive]: schema.status === 'INACTIVE',
                        })}
                      >
                        {schema.status}
                      </span>
                    </td>
                    <td>
                      <span className={styles.fieldCount}>{getFieldCount(schema)}</span>
                    </td>
                    <td>
                      <code className={styles.schemaId}>{schema.id}</code>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        {schema.id === 0 && (
                          <span className={styles.defaultBadge}>Default</span>
                        )}
                        <button
                          className={styles.actionButton}
                          onClick={() => handleToggleExpand(schema.id)}
                          title="View details"
                        >
                          <Eye />
                        </button>
                        {schema.id !== 0 && (
                          <>
                            <button
                              className={cn(styles.actionButton, {
                                [styles.deactivate]: schema.status === 'ACTIVE',
                                [styles.activate]: schema.status === 'INACTIVE',
                              })}
                              onClick={() => handleToggleStatus(schema)}
                              title={schema.status === 'ACTIVE' ? 'Deactivate schema' : 'Activate schema'}
                              disabled={togglingSchemaId === schema.id}
                            >
                              {schema.status === 'ACTIVE' ? <Pause /> : <Play />}
                            </button>
                            <button
                              className={styles.actionButton}
                              onClick={() => handleEditSchema(schema)}
                              title="Edit schema"
                            >
                              <Pencil />
                            </button>
                            <button
                              className={cn(styles.actionButton, styles.delete)}
                              onClick={() => schema.status === 'INACTIVE' && handleDeleteSchema(schema.id)}
                              title={schema.status === 'ACTIVE' ? 'Schema should be deactivated before deletion' : 'Delete schema'}
                              disabled={schema.status === 'ACTIVE'}
                            >
                              <Trash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedSchemaId === schema.id && renderSchemaDetail(schema)}
                </React.Fragment>
              ))}
            </Table>
          )}
        </section>
      </div>
    </div>
  );
};

export default SchemaSettings;
